/**
 * DiagnosticSection Component
 * Admin section for managing diagnostic form templates
 *
 * Features:
 * - Create/Edit diagnostic templates
 * - Assign diagnostic forms to individual employees
 * - Select evaluation type (self or colleague)
 * - View current assignments and submissions
 *
 * Note: Employee requests are managed by company owners/admins
 * via the Manage Diagnostics page (not in admin panel)
 */

import React, { useState } from "react";
import {
  useGetDiagnosticTemplatesQuery,
  useDeleteDiagnosticTemplateMutation,
  useAssignDiagnosticTemplateToCompanyMutation,
  useUnassignDiagnosticTemplateFromCompanyMutation,
  useLazyGetDiagnosticTemplateByIdQuery,
} from "../../../services/api";
import DiagnosticTemplateFormModal from "./DiagnosticTemplateFormModal";

// ============================================
// SVG ICONS
// ============================================
const IconEdit = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const IconTrash = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

/**
 * DiagnosticSection Component
 * @param {string} companyId - Company ID
 * @param {Function} onViewAnswers - Callback when "View Answers" is clicked
 */
const DiagnosticSection = ({ companyId, onViewAnswers }) => {
  // ============================================
  // STATE
  // ============================================
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, template: null });
  const [assignModal, setAssignModal] = useState({ isOpen: false, template: null, action: null });

  // ============================================
  // API HOOKS
  // ============================================
  const { data: templatesData, isLoading: templatesLoading, refetch: refetchTemplates } =
    useGetDiagnosticTemplatesQuery();
  const templates = templatesData?.data?.templates || templatesData?.templates || [];

  const [deleteTemplate] = useDeleteDiagnosticTemplateMutation();
  const [assignToCompany, { isLoading: isAssigning }] = useAssignDiagnosticTemplateToCompanyMutation();
  const [unassignFromCompany, { isLoading: isUnassigning }] = useUnassignDiagnosticTemplateFromCompanyMutation();
  const [fetchTemplateById, { isLoading: isLoadingTemplate }] = useLazyGetDiagnosticTemplateByIdQuery();

  // Check if template is assigned to this company
  const isTemplateAssignedToCompany = (template) => {
    if (!template.assignedCompanies) return false;
    return template.assignedCompanies.some(
      (a) => (a.company === companyId || a.company?._id === companyId) && a.isActive
    );
  };

  // ============================================
  // HANDLERS
  // ============================================
  const handleEditClick = async (template) => {
    try {
      const fullTemplate = await fetchTemplateById(template._id).unwrap();
      setEditingTemplate(fullTemplate);
      setIsFormModalOpen(true);
    } catch (error) {
      console.error("Failed to fetch template:", error);
      alert(error?.data?.message || "Failed to load template. Please try again.");
    }
  };

  const handleDeleteClick = (template) => {
    setDeleteModal({ isOpen: true, template });
  };

  const handleConfirmDelete = async () => {
    if (!deleteModal.template) return;
    try {
      await deleteTemplate(deleteModal.template._id).unwrap();
      refetchTemplates();
      setDeleteModal({ isOpen: false, template: null });
    } catch (error) {
      console.error("Failed to delete template:", error);
      alert(error?.data?.message || "Failed to delete template. Please try again.");
    }
  };

  const handleOpenAssignModal = (template) => {
    const isAssigned = isTemplateAssignedToCompany(template);
    setAssignModal({
      isOpen: true,
      template,
      action: isAssigned ? "unassign" : "assign",
    });
  };

  const handleCloseAssignModal = () => {
    setAssignModal({ isOpen: false, template: null, action: null });
  };

  const handleConfirmAssignment = async () => {
    if (!assignModal.template) return;

    try {
      if (assignModal.action === "assign") {
        await assignToCompany({
          templateId: assignModal.template._id,
          companyId,
        }).unwrap();
      } else {
        await unassignFromCompany({
          templateId: assignModal.template._id,
          companyId,
        }).unwrap();
      }
      refetchTemplates();
      handleCloseAssignModal();
    } catch (error) {
      console.error("Failed to update assignment:", error);
      alert(error?.data?.message || "Failed to update assignment. Please try again.");
    }
  };

  const handleModalClose = () => {
    setIsFormModalOpen(false);
    setEditingTemplate(null);
    refetchTemplates();
  };

  // ============================================
  // RENDER
  // ============================================
  return (
    <>
      {/* Main Section - Matches Unpack Values Pattern */}
      <div className="cd-card-block cd-no-padding">
        <div className="cd-section-header-simple">
          <div className="cd-section-title-main">Diagnostic Forms</div>
          <button
            className="cd-add-btn"
            onClick={() => {
              setEditingTemplate(null);
              setIsFormModalOpen(true);
            }}
          >
            + Add New
          </button>
        </div>

        <div className="cd-section-subtitle">Create and manage diagnostic form templates</div>

        {/* Loading State */}
        {templatesLoading && (
          <div className="cd-loading">Loading templates...</div>
        )}

        {/* Empty State */}
        {!templatesLoading && templates.length === 0 && (
          <div className="cd-empty-state">
            <p>No diagnostic templates available. Create one using "+ Add New".</p>
          </div>
        )}

        {/* Templates Grid */}
        {!templatesLoading && templates.length > 0 && (
          <div className="cd-diagnostic-grid">
            {templates.map((template) => (
              <div key={template._id} className="cd-diagnostic-card">
                {/* Edit/Delete Icons */}
                <div className="cd-card-icons">
                  <button
                    className="cd-icon-btn cd-edit-icon"
                    onClick={() => handleEditClick(template)}
                    disabled={isLoadingTemplate}
                    title="Edit"
                  >
                    <IconEdit />
                  </button>
                  <button
                    className="cd-icon-btn cd-delete-icon"
                    onClick={() => handleDeleteClick(template)}
                  >
                    <IconTrash />
                  </button>
                </div>

                {/* Title */}
                <div className="cd-diagnostic-head">
                  <div className="cd-diagnostic-title">{template.title}</div>
                </div>

                {/* Description */}
                <div className="cd-diagnostic-desc">
                  {template.description || "No description provided."}
                </div>

                {/* Meta Info */}
                <div className="cd-diagnostic-meta">
                  <span className="cd-template-status" data-status={template.status}>
                    {template.status || "draft"}
                  </span>
                  {isTemplateAssignedToCompany(template) && (
                    <span className="cd-assigned-badge">✓ Assigned</span>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="cd-diagnostic-actions">
                  {template.status === "published" && (
                    <button
                      className={`cd-assign-btn ${isTemplateAssignedToCompany(template) ? "cd-unassign-btn" : ""}`}
                      onClick={() => handleOpenAssignModal(template)}
                    >
                      {isTemplateAssignedToCompany(template) ? "Unassign" : "Assign →"}
                    </button>
                  )}
                  <button
                    className="cd-open-btn"
                    onClick={() => onViewAnswers && onViewAnswers(template._id)}
                  >
                    View Answers →
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Template Form Modal */}
      {isFormModalOpen && (
        <DiagnosticTemplateFormModal
          template={editingTemplate}
          companyId={companyId}
          onClose={handleModalClose}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.isOpen && (
        <div className="cd-modal-overlay" onClick={() => setDeleteModal({ isOpen: false, template: null })}>
          <div className="cd-modal-content cd-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="cd-modal-header">
              <h2 className="cd-modal-title">Delete Template</h2>
              <button
                className="cd-modal-close"
                onClick={() => setDeleteModal({ isOpen: false, template: null })}
              >
                ✕
              </button>
            </div>
            <div className="cd-modal-body">
              <p>Are you sure you want to delete "{deleteModal.template?.title}"?</p>
              <p className="cd-modal-warning">This action cannot be undone.</p>
            </div>
            <div className="cd-modal-footer">
              <button
                className="cd-modal-btn cd-modal-btn-cancel"
                onClick={() => setDeleteModal({ isOpen: false, template: null })}
              >
                Cancel
              </button>
              <button
                className="cd-modal-btn cd-modal-btn-danger"
                onClick={handleConfirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assignment Confirmation Modal */}
      {assignModal.isOpen && assignModal.template && (
        <div className="cd-modal-overlay" onClick={handleCloseAssignModal}>
          <div className="cd-modal-content cd-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="cd-modal-header">
              <h2 className="cd-modal-title">
                {assignModal.action === "assign" ? "Assign Template" : "Unassign Template"}
              </h2>
              <button className="cd-modal-close" onClick={handleCloseAssignModal}>
                ✕
              </button>
            </div>

            <div className="cd-modal-body">
              {assignModal.action === "assign" ? (
                <>
                  <p>
                    Are you sure you want to assign "{assignModal.template.title}" to this company?
                  </p>
                  <p className="cd-modal-hint">
                    Once assigned, company owners and admins will be able to use this template
                    to assign diagnostic forms to their employees.
                  </p>
                </>
              ) : (
                <>
                  <p>
                    Are you sure you want to unassign "{assignModal.template.title}" from this company?
                  </p>
                  <p className="cd-modal-warning">
                    Company owners and admins will no longer be able to use this template for new assignments.
                  </p>
                </>
              )}
            </div>

            <div className="cd-modal-footer">
              <button
                className="cd-modal-btn cd-modal-btn-cancel"
                onClick={handleCloseAssignModal}
              >
                Cancel
              </button>
              <button
                className={`cd-modal-btn ${assignModal.action === "assign" ? "cd-modal-btn-confirm" : "cd-modal-btn-danger"}`}
                onClick={handleConfirmAssignment}
                disabled={isAssigning || isUnassigning}
              >
                {isAssigning || isUnassigning
                  ? "Processing..."
                  : assignModal.action === "assign"
                    ? "Confirm & Assign"
                    : "Unassign"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DiagnosticSection;
